import { jsPDF } from 'jspdf';

/**
 * Premium PDF Generator for Testamentair.com
 * Designed to match the Private Banking / Legal-Tech aesthetic.
 * Focus: Whitespace, Elegant Typography, Minimalist Luxury.
 * Disclaimer Fix: Left-aligned, natural spacing, increased right margin.
 */
export const generateReportPDF = async (data) => {
  const { date, analysis, userData } = data;
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4'
  });

  // --- BRAND CONFIGURATION ---
  const colors = {
    navy: [15, 23, 42],      // Slate 900 / Navy
    gold: [194, 155, 85],    // Brand Gold
    slate: [71, 85, 105],    // Slate 600
    subtle: [148, 163, 184], // Slate 400
    bg: [248, 250, 252],     // Slate 50
    border: [226, 232, 240], // Slate 200
    white: [255, 255, 255]
  };

  const margin = 25;
  const pageWidth = doc.internal.pageSize.getWidth();
  const contentWidth = pageWidth - (margin * 2);
  
  // --- HELPER FUNCTIONS ---
  const drawFooter = (pageNum, totalPages) => {
    const footerY = 285;
    doc.setDrawColor(...colors.border);
    doc.setLineWidth(0.1);
    doc.line(margin, footerY - 5, pageWidth - margin, footerY - 5);
    
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...colors.subtle);
    doc.text('VERTROUWELIJK STRATEGISCH DOSSIER', margin, footerY);
    doc.text(`TESTAMENTAIR.COM • PAGINA ${pageNum} VAN ${totalPages}`, pageWidth - margin, footerY, { align: 'right' });
  };

  const drawSectionHeader = (title, y) => {
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...colors.gold);
    doc.text(title.toUpperCase(), margin, y);
    doc.setDrawColor(...colors.gold);
    doc.setLineWidth(0.4);
    doc.line(margin, y + 2, margin + 10, y + 2);
    return y + 12;
  };

  // --- PAGE 1: COVER ---
  doc.setFillColor(...colors.bg);
  doc.rect(0, 0, 15, 297, 'F');
  
  doc.setFillColor(...colors.navy);
  doc.roundedRect(margin, 25, 8, 8, 1.5, 1.5, 'F');
  doc.setTextColor(...colors.navy);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Testamentair', margin + 12, 31);

  doc.setDrawColor(...colors.border);
  doc.setLineWidth(0.2);
  doc.roundedRect(pageWidth - margin - 35, 25, 35, 8, 1, 1, 'D');
  doc.setFontSize(7);
  doc.setTextColor(...colors.slate);
  doc.text('PRIVÉ & VERTROUWELIJK', pageWidth - margin - 17.5, 30, { align: 'center' });

  doc.setFontSize(38);
  doc.setTextColor(...colors.navy);
  doc.setFont('helvetica', 'bold');
  doc.text('Strategisch', margin, 80);
  doc.text('Nalatenschaps-', margin, 96);
  doc.text('dossier', margin, 112);

  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...colors.slate);
  doc.text('Persoonlijke risico-analyse en fiscale optimalisatie', margin, 125);

  doc.setFillColor(...colors.bg);
  doc.roundedRect(margin, 160, contentWidth, 40, 2, 2, 'F');
  
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...colors.subtle);
  doc.text('VOORBEREID VOOR', margin + 10, 172);
  doc.text('DATUM VAN UITGIFTE', margin + 110, 172);

  doc.setFontSize(11);
  doc.setTextColor(...colors.navy);
  doc.text(userData.email || 'Gevalideerd Cliëntprofiel', margin + 10, 182);
  doc.text(date.toUpperCase(), margin + 110, 182);

  doc.setDrawColor(...colors.gold);
  doc.setLineWidth(0.5);
  doc.circle(pageWidth / 2, 240, 22, 'D');
  
  doc.setFontSize(24);
  doc.setFont('helvetica', 'bold');
  doc.text(`${analysis?.score ?? 0}`, pageWidth / 2, 242, { align: 'center' });
  doc.setFontSize(8);
  doc.setTextColor(...colors.gold);
  doc.text('RISICOSCORE', pageWidth / 2, 250, { align: 'center' });

  drawFooter(1, 3);

  // --- PAGE 2: EXECUTIVE SUMMARY ---
  doc.addPage();
  let yPos = 35;
  yPos = drawSectionHeader('Executive Summary', yPos);

  doc.setFontSize(22);
  doc.setTextColor(...colors.navy);
  doc.setFont('helvetica', 'bold');
  doc.text('Status: ' + (analysis.status || 'Aandacht Vereist'), margin, yPos);
  yPos += 15;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...colors.slate);
  const introText = "Op basis van de verstrekte gegevens hebben wij een analyse uitgevoerd van uw nalatenschapsstructuur. De onderstaande score representeert de mate van juridische en fiscale kwetsbaarheid in uw huidige situatie.";
  const lines = doc.splitTextToSize(introText, contentWidth);
  doc.text(lines, margin, yPos);
  yPos += (lines.length * 5) + 15;

  const metrics = [
    { label: 'Partnerbescherming', val: analysis?.protection?.partner ?? 0 },
    { label: 'Kinderbescherming', val: analysis?.protection?.children ?? 0 },
    { label: 'Fiscaal Vermogensbehoud', val: analysis?.protection?.assets ?? 0 }
  ];

  metrics.forEach((m, i) => {
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...colors.navy);
    doc.text(m.label, margin, yPos);
    doc.setFontSize(9);
    doc.text(`${m.val}%`, pageWidth - margin, yPos, { align: 'right' });
    yPos += 3;
    doc.setFillColor(...colors.bg);
    doc.roundedRect(margin, yPos, contentWidth, 2, 1, 1, 'F');
    doc.setFillColor(...colors.navy);
    doc.roundedRect(margin, yPos, (contentWidth * m.val) / 100, 2, 1, 1, 'F');
    yPos += 12;
  });

  yPos += 10;
  yPos = drawSectionHeader('Strategische Vaststellingen', yPos);

  const insights = [
    { title: 'Grootste Risico', val: analysis?.insights?.risk, color: colors.navy },
    { title: 'Kwetsbaarheid', val: analysis?.insights?.vulnerability, color: colors.slate },
    { title: 'Opportuniteit', val: analysis?.insights?.opportunity, color: colors.gold }
  ];

  insights.forEach(ins => {
    doc.setDrawColor(...colors.border);
    doc.setLineWidth(0.1);
    doc.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 8;
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...colors.subtle);
    doc.text(ins.title.toUpperCase(), margin, yPos);
    doc.setFontSize(10);
    doc.setTextColor(...colors.navy);
    doc.text(ins.val || 'Niet geclassificeerd', margin + 45, yPos);
    yPos += 8;
  });

  drawFooter(2, 3);

  // --- PAGE 3: RISICO ANALYSE & ADVIES ---
  doc.addPage();
  yPos = 35;
  yPos = drawSectionHeader('Gedetailleerde Risico-Analyse', yPos);

  const risks = analysis.risks || [
    { title: 'Wettelijke Verdeling', desc: 'Uw huidige situatie valt onder de generieke wetgeving. Dit sluit niet aan bij uw specifieke wensen voor vermogensbehoud.' },
    { title: 'Fiscale Druk', desc: 'Zonder specifieke clausules zoals een opvullegaat loopt de belastingdruk voor uw erfgenamen onnodig op.' }
  ];

  risks.forEach(risk => {
    doc.setFillColor(...colors.bg);
    doc.roundedRect(margin, yPos, contentWidth, 25, 2, 2, 'F');
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...colors.navy);
    doc.text(risk.title, margin + 8, yPos + 10);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...colors.slate);
    const riskLines = doc.splitTextToSize(risk.desc, contentWidth - 16);
    doc.text(riskLines, margin + 8, yPos + 17);
    yPos += 35;
  });

  yPos = drawSectionHeader('Aanbevolen Vervolgstappen', yPos);
  
  const steps = [
    "Plan een intakegesprek bij een gespecialiseerde notaris.",
    "Bespreek de implementatie van een 'vruchtgebruik-testament'.",
    "Evalueer uw schenkingsplan om de erfbelasting jaarlijks te verlagen.",
    "Update uw digitale nalatenschapsregister."
  ];

  steps.forEach((step, i) => {
    doc.setFontSize(10);
    doc.setTextColor(...colors.navy);
    doc.setFont('helvetica', 'bold');
    doc.text(`${i + 1}.`, margin, yPos);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...colors.slate);
    doc.text(step, margin + 8, yPos);
    yPos += 10;
  });

  // --- FIXED TYPOGRAPHY & LAYOUT FOR DISCLAIMER ---
  const footerDividerY = 280;
  doc.setFontSize(6.5);
  doc.setTextColor(...colors.subtle);
  const disclaimer = "DISCLAIMER: Dit dossier is een strategische voorbereiding en vervangt geen officieel juridisch advies of notariële akte. Testamentair.com aanvaardt geen aansprakelijkheid voor de juridische geldigheid van uw huidige situatie zonder tussenkomst van een notaris.";
  
  // Set width to 80% of content area for better whitespace on the right
  const discLines = doc.splitTextToSize(disclaimer, contentWidth * 0.8);
  const discLineHeight = 3.5;
  const totalDiscHeight = discLines.length * discLineHeight;
  const disclaimerStartY = footerDividerY - totalDiscHeight - 5;
  
  // Use left alignment for natural word spacing
  doc.text(discLines, margin, disclaimerStartY, { align: 'left', lineHeightFactor: 1.3 });

  drawFooter(3, 3);

  doc.save(`Strategisch_Nalatenschapsdossier_${userData.email || 'Cliënt'}.pdf`);
};